<ng-template #notification>
  <ng-content select="fcom-notification, [notifications]" />
</ng-template>

<div class="wrapper flex flex--column rounded-large border">
  <div class="card card-container flex">
    @if (image()) {
      <div class="product-image relative">
        <fcom-image
          class="picture"
          [ratio]="imageRatio()"
          [alt]="image().alt"
          [fillParent]="true"
          [imageData]="image()"
          [style.minHeight.px]="notifications?.clientHeight"
        />
        <div class="notification mr-small has-image" #notifications>
          <ng-container *ngTemplateOutlet="notification"></ng-container>
        </div>
      </div>
    }

    <div
      class="details font-body-1 white-bg surface-primary ps-medium flex-1 font-body-medium"
      [class.has-image]="!!image()"
    >
      @if (!image()) {
        <div class="notification mr-xsmall-x" #notifications>
          <ng-container *ngTemplateOutlet="notification"></ng-container>
        </div>
      }

      <div class="flex flex--space-between flex--align-start">
        <div class="destinations font-heading-3 nordic-blue-900-text text-icon-primary font-headline-small">
          @if (title()) {
            <span>{{ title() }}</span>
          } @else {
            <span [title]="destinations() ? (destinations() | finMap: 'name')?.join(' - ') : ''">
              @for (pair of destinationPairs(); track $index; let last = $last) {
                <span class="destination ms-xsmall-r">
                  <span>{{ pair[0] }}</span>
                  <fcom-icon class="icon-medium ms-xxsmall-x" [name]="journeyIcon()" />
                  <span>{{ pair[1] }}</span>
                  @if (!last) {
                    <span>, </span>
                  }
                </span>
              }
            </span>
          }
        </div>
        @if (showExpandButton()) {
          <button
            [attr.aria-label]="(expanded() ? 'tripCard.ariaHide' : 'tripCard.ariaShow') | finLocalization"
            [attr.aria-controls]="expandContainerId"
            [attr.aria-expanded]="expanded()"
            [id]="expandButtonId"
            (click)="toggleExpand($event)"
            class="expand-details ms-small-l nordic-blue-900-text text-icon-primary"
            data-testid="trip-card-expander-toggle-button"
          >
            <fcom-icon
              [class.rotate-active]="expanded()"
              class="icon-medium rotate"
              [name]="SvgLibraryIcon.CHEVRON_DOWN"
            />
          </button>
        }
      </div>
      <div
        class="timeline nordic-blue-900-text text-icon-primary ms-small-t flex flex--wrap flex--align-center g-xsmall"
      >
        <span>{{ departureDate() }}{{ arrivalDate() ? ' - ' + arrivalDate() : '' }}</span>
        @if (journeyType()) {
          <span class="separator nordic-blue-400-text text-icon-secondary" aria-hidden="true">/</span>
          <span>{{ 'tripCard.tripType.' + journeyType() | finLocalization }}</span>
        }
        @if (passengerAmount()) {
          <span class="separator nordic-blue-400-text text-icon-secondary" aria-hidden="true">/</span>
          <span class="passengers flex flex--align-center">
            <fcom-icon class="icon-medium ms-xxsmall-r" [name]="SvgLibraryIcon.MULTI_PAX" aria-hidden="true" />
            <span aria-hidden="true">{{ passengerAmount() }}</span>
            <span class="is-vishidden">
              {{
                (passengerAmount() === 1 ? 'tripCard.passenger' : 'tripCard.passengers')
                  | finLocalization: { passengerAmount: passengerAmount() }
              }}
            </span>
          </span>
        }
      </div>

      @if (!showExpandButton()) {
        <div
          class="flex flex--space-between flex--align-center ms-small-t font-heading-4 nordic-blue-900-text text-icon-primary font-title-medium"
        >
          @if (totalPoints() || flightTotalPriceAmount()) {
            <div>
              @if (totalPoints()) {
                <span data-testid="trip-card-total-points">
                  {{ 'award.points' | finLocalization: { pointsAmount: totalPoints() || 0 } }}&nbsp;+
                </span>
              }
              @if (flightTotalPriceAmount()) {
                <span class="total-amount" data-testid="trip-total-amount">
                  {{ flightTotalPriceAmount() }}
                </span>
              }
            </div>
          }
          @if (showStartOverButton()) {
            <fcom-button
              data-testid="trip-card-modify-button"
              class="start-over"
              (clickBtn)="clickCta($event)"
              [icon]="{ name: SvgLibraryIcon.ARROW_BACK, position: IconPosition.RIGHT }"
              [text]="'tripCard.startOver' | finLocalization"
              [theme]="ButtonTheme.LIGHT_TERTIARY_GHOST"
            />
          }
        </div>
      }
    </div>
  </div>

  @if (showExpandButton()) {
    <div>
      <fcom-expand [open]="expanded()" [ariaId]="expandContainerId" [ariaLabelledBy]="expandButtonId" class="block">
        <div class="expanded-content">
          <ng-content />
        </div>
      </fcom-expand>
    </div>
  }

  @if (bookingReferences().length) {
    <div
      class="flex flex--column g-xxsmall ps-medium-x pr-small-b nordic-blue-900-text text-icon-primary font-body-1 font-body-medium"
      [class.border-top]="expanded()"
      [class.pr-small-t]="expanded()"
    >
      @for (reference of bookingReferences(); track reference.airlineCode + reference.id) {
        <div class="flex flex--align-center g-xsmall">
          @if (reference.tailName) {
            <div class="tail {{ reference.tailName }} flex--nogrow" aria-hidden="true"></div>
          }
          @if (reference.airlineCode) {
            <span aria-hidden="true" data-testid="booking-reference-airline-code">{{ reference.airlineCode }}</span>
            <span class="is-vishidden">{{ reference.airlineName ?? reference.airlineCode }}</span>
          }
          <span>{{ 'tripCard.bookingReference' | finLocalization }}:</span>
          <span class="medium-type font-label-medium" data-testid="booking-reference-value">{{ reference.id }}</span>
          <fin-copy-text
            [value]="reference.id"
            [ariaLabel]="'tripCard.copyBookingReference' | finLocalization"
            [copiedLabel]="'tripCard.bookingReferenceCopied' | finLocalization"
          />
        </div>
      }
    </div>
  }
</div>
